import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const MAX_PHOTO_BYTES = 3 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

const isPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export function InventoryPhotoPicker({
  item,
  pendingFile,
  onPendingChange,
  removeSavedPhoto,
  onRemoveSavedPhotoChange,
}) {
  const [savedSrc, setSavedSrc] = useState("");
  const [previewSrc, setPreviewSrc] = useState("");
  const [sourceOpen, setSourceOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileInput = useRef(null);
  const video = useRef(null);
  const hasSavedPhoto = Boolean(item?.id && item?.has_photo && !removeSavedPhoto);
  const cameraAvailable = Boolean(navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    if (!hasSavedPhoto) {
      setSavedSrc("");
      return undefined;
    }
    let active = true;
    let objectUrl = "";
    api
      .get(`/inventory/${item.id}/photo`, { responseType: "blob" })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (active) {
          setSavedSrc(objectUrl);
        }
      })
      .catch(() => {
        if (active) {
          setSavedSrc("");
        }
      });
    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [hasSavedPhoto, item?.id]);

  useEffect(() => {
    if (!pendingFile) {
      setPreviewSrc("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(pendingFile);
    setPreviewSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pendingFile]);

  useEffect(() => {
    if (cameraOpen && cameraStream && video.current) {
      video.current.srcObject = cameraStream;
    }
  }, [cameraOpen, cameraStream]);

  useEffect(() => () => {
    cameraStream?.getTracks().forEach((track) => track.stop());
  }, [cameraStream]);

  const checkPhoto = (file) => {
    if (!file) {
      return false;
    }
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error("Choose a JPEG, PNG or WebP photo");
      return false;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photos must be 3 MB or smaller");
      return false;
    }
    return true;
  };

  const chooseGalleryPhoto = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!checkPhoto(file)) {
      return;
    }
    onPendingChange(file);
    onRemoveSavedPhotoChange(false);
    setSourceOpen(false);
  };

  const stopCamera = () => {
    cameraStream?.getTracks().forEach((track) => track.stop());
    setCameraStream(null);
    setCameraOpen(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      setCameraStream(stream);
      setSourceOpen(false);
      setCameraOpen(true);
    } catch (error) {
      toast.error(errMsg(error) || "Camera access could not be started");
    }
  };

  const capturePhoto = () => {
    const player = video.current;
    if (!player?.videoWidth || !player.videoHeight) {
      toast.error("Camera is still starting. Please try again.");
      return;
    }
    const maximumDimension = 1600;
    const scale = Math.min(1, maximumDimension / Math.max(player.videoWidth, player.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(player.videoWidth * scale);
    canvas.height = Math.round(player.videoHeight * scale);
    canvas.getContext("2d").drawImage(player, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("The camera photo could not be created");
        return;
      }
      const photo = new File([blob], `inventory-camera-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      if (!checkPhoto(photo)) {
        return;
      }
      onPendingChange(photo);
      onRemoveSavedPhotoChange(false);
      stopCamera();
    }, "image/jpeg", 0.82);
  };

  const removePhoto = () => {
    if (pendingFile) {
      onPendingChange(null);
      return;
    }
    if (hasSavedPhoto) {
      onRemoveSavedPhotoChange(true);
    }
  };

  const imageSrc = previewSrc || savedSrc;
  const hasPreview = Boolean(imageSrc);
  const photoButtonLabel = pendingFile || hasSavedPhoto ? "Replace photo" : "Add photo";
  const galleryLabel = isPhone() ? "Choose from gallery" : "Choose a file";

  return (
    <section className="space-y-3" data-testid="inventory-photo-picker">
      <div
        className="overflow-hidden rounded-lg border bg-muted/20"
        data-testid="inventory-photo-preview-area"
      >
        {hasPreview ? (
          <img
            alt={item?.name ? `${item.name} photo preview` : "Inventory item photo preview"}
            className="aspect-[4/3] w-full object-contain"
            src={imageSrc}
            data-testid="inventory-photo-preview"
          />
        ) : (
          <div
            className="grid aspect-[4/3] place-items-center text-sm text-muted-foreground"
            data-testid="inventory-photo-preview-empty"
          >
            No photo selected
          </div>
        )}
      </div>
      {pendingFile && (
        <p className="text-xs text-muted-foreground" data-testid="inventory-photo-pending-label">
          New photo ready to save: {pendingFile.name}
        </p>
      )}
      {removeSavedPhoto && !pendingFile && (
        <p className="text-xs text-muted-foreground" data-testid="inventory-photo-removal-label">
          This photo will be removed when you save.
        </p>
      )}
      <input
        ref={fileInput}
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={chooseGalleryPhoto}
        type="file"
        data-testid="inventory-photo-gallery-input"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setSourceOpen(true)}
          size="sm"
          type="button"
          data-testid="inventory-photo-source-button"
        >
          <ImagePlus className="mr-2 h-4 w-4" />
          {photoButtonLabel}
        </Button>
        {(pendingFile || hasSavedPhoto) && (
          <Button
            onClick={removePhoto}
            size="sm"
            type="button"
            variant="outline"
            data-testid="inventory-photo-remove-pending-button"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {pendingFile ? "Discard selected photo" : "Remove photo"}
          </Button>
        )}
      </div>
      <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
        <DialogContent data-testid="inventory-photo-source-dialog">
          <DialogHeader>
            <DialogTitle>Add an item photo</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            {cameraAvailable && (
              <Button
                type="button"
                variant="outline"
                onClick={startCamera}
                data-testid="inventory-photo-camera-option"
              >
                <Camera className="mr-2 h-4 w-4" />
                Take a new photo
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInput.current?.click()}
              data-testid="inventory-photo-gallery-option"
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {galleryLabel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={cameraOpen}
        onOpenChange={(open) => {
          if (!open) {
            stopCamera();
          }
        }}
      >
        <DialogContent data-testid="inventory-photo-camera-dialog">
          <DialogHeader>
            <DialogTitle>Take a new photo</DialogTitle>
          </DialogHeader>
          <video
            ref={video}
            autoPlay
            className="aspect-[4/3] w-full rounded-lg bg-black object-cover"
            playsInline
            data-testid="inventory-photo-camera-preview"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={stopCamera}
              data-testid="inventory-photo-camera-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={capturePhoto}
              data-testid="inventory-photo-camera-capture-button"
            >
              <Camera className="mr-2 h-4 w-4" />
              Use photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}